import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Grid, 
  TextField, 
  Button, 
  MenuItem, 
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../contexts/AuthContext';

const serviceTypes = [
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'kitchen', label: 'Kitchen Renovation' },
  { value: 'bathroom', label: 'Bathroom Renovation' },
  { value: 'painting', label: 'Painting' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'cleaning', label: 'Cleaning' }
];

const steps = ['Service Details', 'Property Information', 'Additional Information'];

function QuoteRequest() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const validationSchema = [
    // Step 1 validation
    Yup.object({
      serviceType: Yup.string().required('Service type is required'),
      urgency: Yup.string().required('Urgency level is required'),
      description: Yup.string().required('Description is required').min(10, 'Description should be at least 10 characters')
    }),
    // Step 2 validation
    Yup.object({
      propertyType: Yup.string().required('Property type is required'),
      squareFeet: Yup.number()
        .required('Square footage is required')
        .positive('Square footage must be positive')
        .integer('Square footage must be a whole number'),
      address: Yup.string().required('Address is required')
    }),
    // Step 3 validation
    Yup.object({
      preferredDate: Yup.date().min(new Date(), 'Date cannot be in the past'),
      budget: Yup.number().positive('Budget must be positive'),
      additionalNotes: Yup.string()
    })
  ];
  
  const formik = useFormik({
    initialValues: {
      // Step 1
      serviceType: '',
      urgency: 'normal',
      description: '',
      // Step 2
      propertyType: 'residential',
      squareFeet: '',
      address: '',
      // Step 3
      preferredDate: '',
      budget: '',
      additionalNotes: ''
    },
    validationSchema: validationSchema[activeStep],
    onSubmit: async (values) => {
      if (activeStep < steps.length - 1) {
        setActiveStep(activeStep + 1);
      } else {
        try {
          setLoading(true);
          setError('');
          
          // Log the quote request data
          console.log('Submitting quote request:', values);
          
          // Import the API service
          const api = (await import('../services/api')).default;
          
          // Get the provider ID from the React Router location state or localStorage
          // This could come from a URL parameter, context, or state
          const providerId = location.state?.providerId || localStorage.getItem('selectedProviderId');
          
          // Make the actual API call to create a quote
          const response = await api.createQuote({
            job_type: values.serviceType,
            square_feet: values.squareFeet,
            address: values.address,
            customer_name: values.name || 'Test User',
            customer_email: values.email || 'test@example.com',
            customer_phone: values.phone || '555-123-4567',
            firebase_user_id: currentUser?.uid, // Include Firebase user ID from component level
            provider_id: providerId // Include the provider ID if available
          });
          
          console.log('Quote response:', response.data);
          
          // Navigate to success page or dashboard
          navigate('/dashboard', { 
            state: { 
              success: true, 
              message: 'Quote request submitted successfully! We will get back to you shortly.',
              quoteId: response.data?.id
            } 
          });
        } catch (err) {
          setError('Failed to submit quote request: ' + (err.message || 'Please try again'));
        } finally {
          setLoading(false);
        }
      }
    }
  });
  
  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };
  
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={formik.touched.serviceType && Boolean(formik.errors.serviceType)}>
                <InputLabel id="service-type-label">Service Type</InputLabel>
                <Select
                  labelId="service-type-label"
                  id="serviceType"
                  name="serviceType"
                  value={formik.values.serviceType}
                  onChange={formik.handleChange}
                  label="Service Type"
                >
                  {serviceTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.serviceType && formik.errors.serviceType && (
                  <FormHelperText>{formik.errors.serviceType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={formik.touched.urgency && Boolean(formik.errors.urgency)}>
                <InputLabel id="urgency-label">Urgency</InputLabel>
                <Select
                  labelId="urgency-label"
                  id="urgency"
                  name="urgency"
                  value={formik.values.urgency}
                  onChange={formik.handleChange}
                  label="Urgency"
                >
                  <MenuItem value="low">Low - Within a month</MenuItem>
                  <MenuItem value="normal">Normal - Within two weeks</MenuItem>
                  <MenuItem value="high">High - Within a week</MenuItem>
                  <MenuItem value="urgent">Urgent - As soon as possible</MenuItem>
                </Select>
                {formik.touched.urgency && formik.errors.urgency && (
                  <FormHelperText>{formik.errors.urgency}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description of the service needed"
                multiline
                rows={4}
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={formik.touched.propertyType && Boolean(formik.errors.propertyType)}>
                <InputLabel id="property-type-label">Property Type</InputLabel>
                <Select
                  labelId="property-type-label"
                  id="propertyType"
                  name="propertyType"
                  value={formik.values.propertyType}
                  onChange={formik.handleChange}
                  label="Property Type"
                >
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                  <MenuItem value="industrial">Industrial</MenuItem>
                </Select>
                {formik.touched.propertyType && formik.errors.propertyType && (
                  <FormHelperText>{formik.errors.propertyType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="squareFeet"
                name="squareFeet"
                label="Square Footage"
                type="number"
                value={formik.values.squareFeet}
                onChange={formik.handleChange}
                error={formik.touched.squareFeet && Boolean(formik.errors.squareFeet)}
                helperText={formik.touched.squareFeet && formik.errors.squareFeet}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="address"
                name="address"
                label="Property Address"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="preferredDate"
                name="preferredDate"
                label="Preferred Service Date"
                type="date"
                value={formik.values.preferredDate}
                onChange={formik.handleChange}
                error={formik.touched.preferredDate && Boolean(formik.errors.preferredDate)}
                helperText={formik.touched.preferredDate && formik.errors.preferredDate}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="budget"
                name="budget"
                label="Budget (Optional)"
                type="number"
                value={formik.values.budget}
                onChange={formik.handleChange}
                error={formik.touched.budget && Boolean(formik.errors.budget)}
                helperText={formik.touched.budget && formik.errors.budget}
                InputProps={{
                  startAdornment: <Box component="span" mr={0.5}>$</Box>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="additionalNotes"
                name="additionalNotes"
                label="Additional Notes (Optional)"
                multiline
                rows={4}
                value={formik.values.additionalNotes}
                onChange={formik.handleChange}
                error={formik.touched.additionalNotes && Boolean(formik.errors.additionalNotes)}
                helperText={formik.touched.additionalNotes && formik.errors.additionalNotes}
              />
            </Grid>
          </Grid>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Request a Quote
      </Typography>
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <form onSubmit={formik.handleSubmit}>
          {getStepContent(activeStep)}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep !== 0 && (
              <Button 
                onClick={handleBack} 
                sx={{ mr: 1 }}
                disabled={loading}
              >
                Back
              </Button>
            )}
            <Button
              variant="contained"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : activeStep === steps.length - 1 ? (
                'Submit'
              ) : (
                'Next'
              )}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

export default QuoteRequest;
